import { useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useInviteMember } from '../../mutations';
import { useDepartmentsRequired, useRoles } from '../../queries';
import type { Invitation } from '../../types';
import { CatalogSelect } from '../CatalogSelect';
import { DepartmentSelect } from '../DepartmentSelect';
import { JobTitleSelect } from '../JobTitleSelect';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { Input } from '@/shared/ui/atoms/Input';
import { Select } from '@/shared/ui/atoms/Select';
import { FormField } from '@/shared/ui/molecules/FormField';
import { formatDate } from '@/shared/utils/locale';
import '../../../tasks/ui/NewTaskForm/NewTaskForm.styl';
import '../../../platform/ui/InviteAdminForm/InviteAdminForm.styl';

type Props = { onDone: () => void };

export function InviteMemberForm({ onDone }: Props) {
  const invite = useInviteMember();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const roles = useRoles();
  const [roleId, setRoleId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [memberTypeId, setMemberTypeId] = useState('');
  const [contractTypeId, setContractTypeId] = useState('');
  const departmentRequired = useDepartmentsRequired();
  const [result, setResult] = useState<Invitation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setError(t('people.invalidEmail'));
    if (!jobTitle) return setError(t('people.jobTitleRequired'));
    if (departmentRequired && !departmentId) return setError(t('departments.required'));
    setError(null);
    invite.mutate({ email: email.trim(), ...(roleId ? { roleId } : { role: 'MEMBER' as const }), ...(displayName.trim() ? { displayName: displayName.trim() } : {}), jobTitle, ...(departmentId ? { departmentId } : {}), ...(memberTypeId ? { memberTypeId } : {}), ...(contractTypeId ? { contractTypeId } : {}) }, { onSuccess: setResult, onError: (err) => setError(errorMessage(err, t('people.inviteError'))) });
  };

  if (result) {
    return (
      <div className="f-invite-admin">
        <p className="f-invite-admin__ok">
          <Trans i18nKey="people.sentTo" values={{ email: result.email, date: formatDate(result.expiresAt) }} components={{ b: <b /> }} />
        </p>
        {result.inviteUrl ? (
          <div className="f-invite-admin__link">
            {result.emailDelivered === false ? (
              <div className="f-invite-admin__warning" role="alert">
                {t('people.mailFailed')}
              </div>
            ) : null}
            <div className="f-invite-admin__link-label">{t('people.devLink')}</div>
            <code className="u-mono f-invite-admin__url">{result.inviteUrl}</code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(result.inviteUrl ?? '').then(() => setCopied(true));
              }}
            >
              {copied ? t('common.copied') : t('common.copyLink')}
            </Button>
          </div>
        ) : null}
        <div className="f-newtask__actions">
          <Button onClick={onDone}>{t('common.done')}</Button>
        </div>
      </div>
    );
  }

  return (
    <form className="f-newtask" onSubmit={submit} noValidate>
      <div className="f-newtask__row">
        <FormField label={t('people.email')} htmlFor="im-email">
          <Input icon="envelop2" id="im-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('people.emailPlaceholder')} autoFocus />
        </FormField>
        <FormField label={t('people.role')} htmlFor="im-role">
          <Select icon="key2" id="im-role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">{t('roles.MEMBER')}</option>
            {(roles.data ?? []).filter((r) => r.kind !== 'MEMBER').map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </FormField>
      </div>
      <div className="f-newtask__row">
        <FormField label={t('people.nameOptional')} htmlFor="im-name">
          <Input icon="user4" id="im-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </FormField>
        <FormField label={t('people.jobTitle')} htmlFor="im-title">
          <JobTitleSelect id="im-title" value={jobTitle} onChange={setJobTitle} />
        </FormField>
      </div>
      <FormField label={t('departments.label')} htmlFor="im-department">
        <DepartmentSelect id="im-department" value={departmentId} onChange={setDepartmentId} />
      </FormField>
      <div className="f-newtask__row">
        <FormField label={t('catalogs.member-types.label')} htmlFor="im-member-type">
          <CatalogSelect kind="member-types" id="im-member-type" value={memberTypeId} onChange={setMemberTypeId} />
        </FormField>
        <FormField label={t('catalogs.contract-types.label')} htmlFor="im-contract-type">
          <CatalogSelect kind="contract-types" id="im-contract-type" value={contractTypeId} onChange={setContractTypeId} />
        </FormField>
      </div>
      {error ? (
        <div className="f-newtask__error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="f-newtask__actions">
        <Button variant="cancel" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={invite.isPending}>
          {t('people.sendInvitation')}
        </Button>
      </div>
    </form>
  );
}
